import { CONTENT_CELL_WIDTH } from '@ui/ElementList/list.constants';

/**
 * Named container for project-list column collapse queries.
 * Set `container-type: inline-size` on the list wrapper.
 */
export const PROJECT_LIST_CONTAINER_NAME = 'project-list';

/**
 * Hide priority for project-list columns. First entry hides first (widest
 * list). Last entry hides last. Reorder this array to change which columns
 * disappear first. Project name is never listed and never hides.
 *
 * Extra delay in {@link PROJECT_LIST_LATE_HIDE_EXTRA_BY_COLUMN} is applied
 * after the index formula and does not follow array order.
 */
export const PROJECT_LIST_COLUMN_HIDE_PRIORITY = [
  'owner',
  'completedAt',
  'cost',
  'gfa',
  'updatedAt',
  'co2',
  'bars',
] as const;

export type ProjectListCollapsibleColumn =
  (typeof PROJECT_LIST_COLUMN_HIDE_PRIORITY)[number];

/** Widest list width (px) at which the first-priority column hides. */
export const PROJECT_LIST_HIDE_START_PX = 1600;

/** Each later entry in {@link PROJECT_LIST_COLUMN_HIDE_PRIORITY} hides this many px narrower. */
export const PROJECT_LIST_HIDE_STEP_PX = 140;

/**
 * Floor for the clickable project name. Kept low so name and bars can shrink
 * before CO₂ columns hide.
 */
export const PROJECT_LIST_NAME_MIN_WIDTH = 80;

/**
 * Floor for the icon + name group after date/GFA in that group have hidden.
 */
export const PROJECT_LIST_NAME_GROUP_MIN_WIDTH =
  CONTENT_CELL_WIDTH.ICON_LARGE + PROJECT_LIST_NAME_MIN_WIDTH;

/** Floor for the CO₂ bar while it is still visible. */
export const PROJECT_LIST_BAR_MIN_WIDTH = 80;

/**
 * Extra px subtracted from the step formula for columns in
 * {@link PROJECT_LIST_LATE_HIDE_EXTRA_BY_COLUMN}.
 */
export const PROJECT_LIST_LATE_HIDE_EXTRA_PX = 220;

/**
 * Extra hide delay by column. Independent of
 * {@link PROJECT_LIST_COLUMN_HIDE_PRIORITY} order.
 */
export const PROJECT_LIST_LATE_HIDE_EXTRA_BY_COLUMN: Partial<
  Record<ProjectListCollapsibleColumn, number>
> = {
  co2: PROJECT_LIST_LATE_HIDE_EXTRA_PX,
  bars: PROJECT_LIST_LATE_HIDE_EXTRA_PX,
};

/** Flex shrink for name and bars so they give way before CO₂ columns hide. */
export const PROJECT_LIST_SHRINK = 3;

/**
 * Pack remaining columns from the start so header and rows keep the same
 * offsets when cells hide (`space-between` would re-distribute independently).
 */
export const PROJECT_LIST_ROW_SX = {
  justifyContent: 'flex-start',
} as const;

/**
 * List width (px) at which {@link column} starts hiding, from its index in
 * {@link PROJECT_LIST_COLUMN_HIDE_PRIORITY} minus any extra in
 * {@link PROJECT_LIST_LATE_HIDE_EXTRA_BY_COLUMN}.
 */
export const getProjectListColumnHideBelow = (
  column: ProjectListCollapsibleColumn,
): number => {
  const index = PROJECT_LIST_COLUMN_HIDE_PRIORITY.indexOf(column);
  const extra = PROJECT_LIST_LATE_HIDE_EXTRA_BY_COLUMN[column] ?? 0;
  return PROJECT_LIST_HIDE_START_PX - index * PROJECT_LIST_HIDE_STEP_PX - extra;
};

/**
 * Max-width used in the `@container` query that hides {@link column}.
 */
export const getProjectListColumnHideMaxWidth = (
  column: ProjectListCollapsibleColumn,
): number => getProjectListColumnHideBelow(column) - 1;

/**
 * Whether {@link column} should be visible at {@link containerWidth}.
 */
export const isProjectListColumnVisible = (
  column: ProjectListCollapsibleColumn,
  containerWidth: number,
): boolean => containerWidth >= getProjectListColumnHideBelow(column);

/**
 * `sx` that hides {@link column} when the project-list container is too narrow.
 */
export const getProjectListColumnHideSx = (
  column: ProjectListCollapsibleColumn,
): Record<string, { display: 'none !important' }> => ({
  [`@container ${PROJECT_LIST_CONTAINER_NAME} (max-width: ${getProjectListColumnHideMaxWidth(column)}px)`]:
    {
      display: 'none !important',
    },
});

/** Name group shrinks aggressively so bars and the CO₂ figure can stay. */
export const getProjectListNameGroupSx = () => ({
  overflow: 'hidden',
  flexShrink: PROJECT_LIST_SHRINK,
});

/** Name cell shrinks with ellipsis instead of pushing out bars/CO₂. */
export const getProjectListNameCellSx = () => ({
  overflow: 'hidden',
  flexShrink: PROJECT_LIST_SHRINK,
});

/** Bars shrink with the name until their later hide breakpoint. */
export const getProjectListBarCellSx = () => ({
  overflow: 'hidden',
  flexShrink: PROJECT_LIST_SHRINK,
  minWidth: PROJECT_LIST_BAR_MIN_WIDTH,
  ...getProjectListColumnHideSx('bars'),
});

/**
 * Shrink the trailing actions cell to the kebab width when owner is hidden.
 * Header and rows both keep this cell so columns stay aligned.
 */
export const getProjectListOwnerActionsSx = () => ({
  [`@container ${PROJECT_LIST_CONTAINER_NAME} (max-width: ${getProjectListColumnHideMaxWidth('owner')}px)`]:
    {
      flexBasis: CONTENT_CELL_WIDTH.ICON,
    },
});
